import { z } from "zod";
import type { Locale } from "./_schema";

const ID = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const UNSAFE_URL_CHAR = /[\u0000-\u0020\u007f\\]/;
const LOCALES = ["en", "es"] as const satisfies readonly Locale[];

function text(max: number) {
  return z.string().min(1).max(max).refine((value) => value.trim().length > 0, "text must not be blank");
}

function safeHttps(value: string): boolean {
  if (UNSAFE_URL_CHAR.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname.length > 0 && !url.username && !url.password;
  } catch {
    return false;
  }
}

function safeAsset(value: string): boolean {
  if (safeHttps(value)) return true;
  if (!value.startsWith("/") || value.startsWith("//") || UNSAFE_URL_CHAR.test(value)) return false;
  try {
    return value.split(/[?#]/)[0]!.split("/").every((part) => {
      const decoded = decodeURIComponent(part);
      return decoded !== "." && decoded !== ".." && !decoded.includes("\\");
    });
  } catch {
    return false;
  }
}

const Paragraphs = z.array(text(12000)).min(1).max(32);
const Highlights = z.array(text(512)).max(24);
const ImageCoordinate = z.number().finite().min(-4096).max(4096);
const ReviewSchema = z.object({
  id: z.string().regex(ID),
  rating: z.number().min(0).max(5),
  heading: text(512),
  paragraphs: Paragraphs,
  author: text(160),
  verified: z.boolean().optional(),
}).strict();

const StoryBlockSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("paragraphs"), paragraphs: Paragraphs, emphasis: Highlights.optional(), accent: Highlights.optional() }).strict(),
  z.object({ type: z.literal("list"), items: z.array(text(12000)).min(1).max(32) }).strict(),
  z.object({
    type: z.literal("timeline"),
    items: z.array(z.object({
      label: text(160),
      title: text(160).optional(),
      paragraphs: Paragraphs,
    }).strict()).min(1).max(24),
  }).strict(),
  z.object({ type: z.literal("reviews"), items: z.array(ReviewSchema).min(1).max(24) }).strict(),
  z.object({ type: z.literal("rating"), stars: z.number().min(0).max(5), text: text(512), label: text(64).optional() }).strict(),
  z.object({ type: z.literal("benefits"), items: z.array(z.object({ icon: z.enum(["guarantee", "shipping"]), text: text(512) }).strict()).min(1).max(8) }).strict(),
]);
export type StoryBlock = z.infer<typeof StoryBlockSchema>;

const StoryFunnelScreenSchema = z.object({
  id: z.string().regex(ID),
  role: z.enum(["story", "offer"]),
  heading: text(512),
  headingAccent: Highlights.optional(),
  contentTop: z.number().finite().min(0).max(4096).optional(),
  eyebrow: text(160).optional(),
  cta: text(160),
  layout: z.enum(["image-overlay", "content-panel"]),
  artwork: z.literal("boost-offer").optional(),
  image: z.object({
    src: text(2048).refine(safeAsset, "image must use HTTPS or a safe root-relative path"), alt: text(512),
    geometry: z.object({
      x: ImageCoordinate, y: ImageCoordinate,
      width: z.number().finite().positive().max(4096), height: z.number().finite().positive().max(4096),
      flipX: z.boolean().optional(),
    }).strict().optional(),
    fade: z.object({ start: z.number().finite().min(0).max(4096), end: z.number().finite().positive().max(4096) }).strict()
      .refine(({ start, end }) => end > start, "image fade must end after it starts").optional(),
  }).strict().optional(),
  blocks: z.array(StoryBlockSchema).min(1).max(24),
  disclaimer: text(12000).optional(),
}).strict();
export type StoryFunnelScreen = z.infer<typeof StoryFunnelScreenSchema>;

export const StoryFunnelSchema = z.object({
  kind: z.literal("story-funnel"),
  slug: z.string().min(1).max(64).regex(/^[a-z0-9-]+$/),
  version: z.number().int().positive(),
  meta: z.object({ title: text(160) }).strict(),
  locale: z.enum(LOCALES).optional(),
  effects: z.object({ confetti: z.boolean() }).strict().optional(),
  offer: z.object({ href: text(2048).refine(safeHttps, "offer destination must be an absolute HTTPS URL without credentials") }).strict(),
  screens: z.array(StoryFunnelScreenSchema).min(2).max(32),
}).strict().superRefine((story, ctx) => {
  const screenIds = new Set<string>();
  let offers = 0;
  story.screens.forEach((screen, index) => {
    if (screenIds.has(screen.id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["screens", index, "id"], message: "screen IDs must be unique" });
    }
    screenIds.add(screen.id);
    if (screen.role === "offer") {
      offers += 1;
      if (index !== story.screens.length - 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["screens", index, "role"], message: "the offer must be the final screen" });
      }
    }
    screen.blocks.forEach((block, blockIndex) => {
      if (block.type !== "reviews") return;
      const reviewIds = new Set<string>();
      block.items.forEach((review, reviewIndex) => {
        if (reviewIds.has(review.id)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["screens", index, "blocks", blockIndex, "items", reviewIndex, "id"], message: "review IDs must be unique within a carousel" });
        }
        reviewIds.add(review.id);
      });
    });
  });
  if (offers !== 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["screens"], message: "a story must end with exactly one offer" });
  }
});

export type StoryFunnel = z.infer<typeof StoryFunnelSchema>;

/** Validate authored data at boot and return the parsed object to the runner. */
export function defineStoryFunnel(story: StoryFunnel): StoryFunnel {
  return StoryFunnelSchema.parse(story);
}
